<script setup lang="ts" generic="T extends CardListItem">
import { type Component, nextTick, useTemplateRef, watch } from 'vue';
import PlusFillIcon from '@/assets/icons/ui/plus-fill.svg';
import type { IconComponent } from '@/modules/common/types/icon.type.ts';
import UiButtonIcon from '@/modules/ui/components/ui/UiButtonIcon.vue';
import UiCard from '@/modules/ui/components/ui/UiCard.vue';

export type CardListItem = {
  id: string | number;
  icon?: IconComponent;
};

const { list, readonly, withAdd, editable, editingId, iconSpaced, closeOnHover, tooltipOnCard, equalHeight } =
  defineProps<{
    list: T[];
    readonly?: boolean;
    withAdd?: boolean;
    editable?: boolean;
    editingId?: T['id'] | null;
    cardFooter?: Component;
    titleKey?: string;
    iconSpaced?: boolean;
    closeOnHover?: boolean;
    tooltipOnCard?: boolean;
    equalHeight?: boolean;
  }>();

defineSlots<{
  card?: (props: { item: T; index: number; readonly?: boolean }) => unknown;
}>();

defineEmits<{
  change: [value: { item: T; changes: unknown }];
  remove: [value: CardListItem['id']];
  edit: [value: T, anchor?: HTMLElement];
  add: [];
}>();

const listRef = useTemplateRef('list');

watch(
  () => list.length,
  async (length, prevLength) => {
    if (length <= prevLength) {
      return;
    }

    await nextTick();
    listRef.value?.scrollTo({ top: listRef.value.scrollHeight, behavior: 'smooth' });
  }
);

function showAdd(index: number) {
  return !readonly && withAdd && index === list.length - 1;
}
</script>

<template>
  <ul
    ref="list"
    class="ui-card-list"
    :class="{ '_field': withAdd, '_equal-height': equalHeight }"
  >
    <li
      v-for="(item, index) in list"
      :key="item.id"
      class="item"
      :class="{ '_equal-height': equalHeight }"
    >
      <slot
        name="card"
        :item="item"
        :index="index"
        :readonly="readonly"
      >
        <UiCard
          v-if="item.icon"
          :item="item"
          :index="index"
          :icon="item.icon"
          :readonly="readonly"
          :editable="editable"
          :active="editingId != null && editingId === item.id"
          :title-key="titleKey"
          :footer-component="cardFooter"
          :icon-spaced="iconSpaced"
          :close-on-hover="closeOnHover"
          :tooltip-on-card="tooltipOnCard"
          @change="$emit('change', { item, changes: $event })"
          @remove="$emit('remove', item.id)"
          @edit="$emit('edit', item, $event)"
        />
      </slot>

      <UiButtonIcon
        v-show="showAdd(index)"
        class="plus"
        color="none"
        @click="$emit('add')"
      >
        <PlusFillIcon />
      </UiButtonIcon>
    </li>
  </ul>
</template>

<style scoped lang="scss">
.ui-card-list {
  display: grid;
  flex-grow: 1;
  padding: 8px;
  gap: 8px;
  align-items: stretch;
  grid-template-columns: repeat(auto-fill, minmax(125px, 1fr));
  grid-auto-rows: min-content;

  &._field {
    padding-right: 40px;
  }

  &._equal-height {
    height: fit-content;
    max-height: 100%;
    grid-auto-rows: 1fr;
  }
}

.item {
  position: relative;
  min-width: 0;

  &._equal-height {
    display: grid;
    grid-template-rows: subgrid;
    grid-row: span 1;

    > :deep(*:not(.plus)) {
      width: 100%;
      min-width: 0;
      height: 100%;
    }
  }
}

.plus {
  position: absolute;
  top: 50%;
  right: -32px;
  transform: translateY(-50%);

  :deep(svg) {
    fill: var(--color-gray-500);
    transition: fill var(--transition-fast);
  }

  @media (hover: hover) {
    &:hover :deep(svg) {
      fill: var(--color-gray-700);
    }
  }
}

.ui-card {
  height: 100%;
}
</style>
